
import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useLazyQuery } from '@apollo/client/react';
import { Box, LinearProgress, Typography, Card, CardContent, Chip } from '@mui/material';
import { CustomButton } from '../../../components/customButton/CustomButton';
import { CANTIDAD_PACIENTES_SIN_GEO } from '../../../data/graphQL/queries/HerramientaGeoposicion/getCantidadPacientesSinGeo';
import { POSICIONAR_PACIENTES_SIN_GEO } from '../../../data/graphQL/mutations/HerramientaGeoposicion/posicionarPacientesSinGeo';
import { OBTENER_ESTADO_GEOPOSICION } from '../../../data/graphQL/queries/HerramientaGeoposicion/getEstadoProcesoGeoposicion';
import { toast } from 'react-hot-toast';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

export const HerramientaGeoposicionContent = () => {
    const [cantidadPacientes, setCantidadPacientes] = useState(0);
    const [mostrarEstado, setMostrarEstado] = useState(false);

    const { data: dataCantidadPacientes } = useQuery(CANTIDAD_PACIENTES_SIN_GEO, {
        fetchPolicy: 'no-cache'
    });

    const [obtenerEstado, { data: dataEstado, loading: loadingEstado }] = useLazyQuery(OBTENER_ESTADO_GEOPOSICION, {
        fetchPolicy: 'no-cache'
    });

    useEffect(() => {
        if (!dataCantidadPacientes) return;
        setCantidadPacientes(dataCantidadPacientes?.cantidadPacientesSinGeoposicion);
    }, [dataCantidadPacientes]);

    const [posicionarPacientesSinGeo, { loading: finalizarLoading }] = useMutation(POSICIONAR_PACIENTES_SIN_GEO, {
        refetchQueries: [{ query: CANTIDAD_PACIENTES_SIN_GEO }],
        onCompleted: (data) => {
            if (data?.resolverGeoposicionDePacientesSin?.success) {
                setMostrarEstado(true);
                // Consultar estado inmediatamente después de iniciar
                obtenerEstado();
            }
        }
    });

    const handleGuardar = () => {
        toast.promise(posicionarPacientesSinGeo(), {
            loading: 'Solicitando...',
            success: 'Se comenzó el proceso de posicionamiento correctamente.',
            error: 'Ocurrió un error.'
        });
    };

    const handleConsultarEstado = () => {
        if (!mostrarEstado) {
            setMostrarEstado(true);
        }
        obtenerEstado();
    };

    const handleOcultarEstado = () => {
        setMostrarEstado(false);
    };

    const estado = dataEstado?.obtenerEstadoGeoposicion;
    const estaEjecutando = estado?.estaEjecutando;
    const progreso = estado?.progreso || 0;

    const formatearTiempo = (tiempoTranscurrido) => {
        if (!tiempoTranscurrido) return '-';
        
        const match = tiempoTranscurrido.match(/(\d+):(\d+):(\d+)/);
        if (!match) return tiempoTranscurrido;
        
        const [, horas, minutos, segundos] = match;
        return `${horas}h ${minutos}m ${segundos}s`;
    };

    const getEstadoChip = () => {
        if (!estado) return null;
        
        if (estado.estaEjecutando) {
            return <Chip label="En Ejecución" color="primary" icon={<PlayArrowIcon />} />;
        } else if (estado.fechaFin) {
            return <Chip label="Completado" color="success" icon={<CheckCircleIcon />} />;
        }
        return <Chip label="Sin información" color="default" />;
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: '16px', padding: '20px' }}>
            <Box sx={{ fontWeight: '500' }}>
                <p>Existen {cantidadPacientes} pacientes que requieren ajustar el geoposicionamiento.</p>
            </Box>
            
            <Box sx={{ display: 'flex', gap: 2 }}>
                <CustomButton 
                    variant="contained"
                    color="primary"
                    onClick={handleGuardar}
                    disabled={cantidadPacientes === 0 || finalizarLoading || estaEjecutando}
                    loading={finalizarLoading}
                    startIcon={<PlayArrowIcon />}
                >
                    Ajustar Geoposicionamiento
                </CustomButton>

                <CustomButton 
                    variant="outlined"
                    color="primary"
                    onClick={handleConsultarEstado}
                    loading={loadingEstado}
                    startIcon={<RefreshIcon />}
                >
                    Consultar Estado
                </CustomButton>

                {mostrarEstado && (
                    <CustomButton 
                        variant="text"
                        color="secondary"
                        onClick={handleOcultarEstado}
                    >
                        Ocultar
                    </CustomButton>
                )}
            </Box>

            {mostrarEstado && estado && (
                <Card sx={{ width: '100%', maxWidth: 600, mt: 2 }}>
                    <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6" component="div">
                                Estado del Proceso
                            </Typography>
                            {getEstadoChip()}
                        </Box>

                        {estaEjecutando && (
                            <>
                                <Box sx={{ mb: 2 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                        <Typography variant="body2" color="text.secondary">
                                            Progreso
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {progreso.toFixed(2)}%
                                        </Typography>
                                    </Box>
                                    <LinearProgress 
                                        variant="determinate" 
                                        value={progreso} 
                                        sx={{ height: 10, borderRadius: 5 }}
                                    />
                                </Box>

                                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">
                                            Total
                                        </Typography>
                                        <Typography variant="h6">
                                            {estado.totalRegistros || 0}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">
                                            Procesados
                                        </Typography>
                                        <Typography variant="h6">
                                            {estado.totalProcesados || 0}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">
                                            Exitosos
                                        </Typography>
                                        <Typography variant="h6" color="success.main">
                                            {estado.exitosos || 0}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">
                                            Fallidos
                                        </Typography>
                                        <Typography variant="h6" color="error.main">
                                            {estado.fallidos || 0}
                                        </Typography>
                                    </Box>
                                </Box>

                                <Box sx={{ p: 1.5, backgroundColor: '#e3f2fd', borderRadius: 1, border: '1px solid #2196f3' }}>
                                    <Typography variant="body2" color="primary">
                                        ℹ️ Haz clic en "Consultar Estado" nuevamente para actualizar el progreso.
                                    </Typography>
                                </Box>
                            </>
                        )}

                        {!estaEjecutando && estado.fechaFin && (
                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        Total Procesado
                                    </Typography>
                                    <Typography variant="h6">
                                        {estado.totalProcesados}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        Exitosos
                                    </Typography>
                                    <Typography variant="h6" color="success.main">
                                        {estado.exitosos}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        Fallidos
                                    </Typography>
                                    <Typography variant="h6" color="error.main">
                                        {estado.fallidos}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        Tasa de Éxito
                                    </Typography>
                                    <Typography variant="h6" color="success.main">
                                        {estado.totalProcesados > 0 
                                            ? ((estado.exitosos / estado.totalProcesados) * 100).toFixed(2) 
                                            : 0}%
                                    </Typography>
                                </Box>
                            </Box>
                        )}

                        <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #e0e0e0' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="caption" color="text.secondary">
                                    Fecha de Inicio
                                </Typography>
                                <Typography variant="body2">
                                    {estado.fechaInicio 
                                        ? new Date(estado.fechaInicio).toLocaleString('es-AR')
                                        : '-'}
                                </Typography>
                            </Box>
                            {estado.fechaFin && (
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                    <Typography variant="caption" color="text.secondary">
                                        Fecha de Fin
                                    </Typography>
                                    <Typography variant="body2">
                                        {new Date(estado.fechaFin).toLocaleString('es-AR')}
                                    </Typography>
                                </Box>
                            )}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="caption" color="text.secondary">
                                    Tiempo Transcurrido
                                </Typography>
                                <Typography variant="body2">
                                    {formatearTiempo(estado.tiempoTranscurrido)}
                                </Typography>
                            </Box>
                        </Box>
                    </CardContent>
                </Card>
            )}

            {mostrarEstado && !estado && !loadingEstado && (
                <Card sx={{ width: '100%', maxWidth: 600, mt: 2 }}>
                    <CardContent>
                        <Typography variant="body2" color="text.secondary" textAlign="center">
                            No hay procesos de geoposición en ejecución ni completados recientemente.
                        </Typography>
                    </CardContent>
                </Card>
            )}
        </div>
    );
}